<!-- ChatInput.vue - Reusable Chat Input Component with Consistent Background -->
<template>
    <div class="chat-input-wrapper">
      <v-card-actions id="userMsg" class="pt-0 pb-0 rounded-message-input" :class="{ 'dark-theme': isDarkTheme }">
        <v-form @submit.prevent="handleSendMessage" id="row">
          <div :id="srMessageId" class="sr-only">
            Enter your message here. Use Enter to send or Shift+Enter for a new line. Maximum {{maxLength}} characters allowed.
          </div>
          <label class="sr-only" :for="inputId">
            {{ inputAriaLabel || 'Message input field' }}
          </label>
          <v-textarea 
            auto-grow
            rows="1"
            no-resize
            hide-details
            @keydown="onKeydown"
            :id="inputId"
            v-model="message" 
            :placeholder="$t('message.how_can_i_help')"
            :aria-describedby="srMessageId"
            :role="textboxRole"
            class="transparent-textarea"
            counter
            :maxLength="maxLength">
            <template v-slot:append>
              <div class="char-count-inside" aria-label="Character count">
                <span>{{ charCount }}/{{ maxLength }}</span>
              </div>
              <div class="send-container">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <button
                      v-bind="props"
                      id="send-button"
                      type="submit"
                      :aria-label="$t('message.send')"
                      :disabled="charCount > maxLength">
                      <!-- <font-awesome-icon :icon="['fas', 'fa-paper-plane']" /> -->
                      <Send />
                    </button>
                  </template>
                  <span>{{ $t('message.send') }}</span>
                </v-tooltip>
              </div>
            </template>
          </v-textarea>
        </v-form>
      </v-card-actions>
      
      <!-- Character count below input -->
      <!-- <div class="char-count-container">
        <div class="char-count-small" aria-label="Character count">
          <span>{{ charCount }}/{{ maxLength }}</span>
        </div>
      </div> -->
    </div>
  </template>
  
  <script>
import { Send } from 'lucide-vue-next';

  export default {
    name: 'ChatInput',
    components: {
      Send
    },
    props: {
      value: {
        type: String,
        default: ''
      },
      maxLength: {
        type: Number,
        default: 100
      },
      inputId: {
        type: String,
        default: 'chat-message'
      },
      srMessageId: {
        type: String,
        default: 'chat-message-messages'
      },
      formAriaLabel: {
        type: String,
        default: 'Your Message Form'
      },
      inputAriaLabel: {
        type: String,
        default: 'Message Input Field'
      },
      textboxRole: {
        type: String,
        default: ''
      },
      isDarkTheme: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        message: this.value
      };
    },
    computed: {
      charCount() {
        return this.message.length;
      }
    },
    watch: {
      value: {
        handler(newVal) {
          // Only update if the value is different from current message
          if (this.message !== newVal) {
            this.message = newVal;
          }
        },
        immediate: true
      },
      message(newVal) {
        this.$emit('input', newVal);
      }
    },
    methods: {
      handleSendMessage() {
        if (this.message.trim()) {
          console.log("handleSendMessage - this message: ", this.message);
          this.$emit('send-message', this.message);
          this.clearMessage();
          // Force update the v-model binding in parent
          this.$nextTick(() => {
            this.$emit('input', '');
          });
        }
      },
      clearMessage() {
        this.message = '';
        // Also emit input event to sync with parent v-model
        this.$emit('input', '');
        // Force the textarea to clear
        this.$nextTick(() => {
          const textarea = this.$el.querySelector('textarea');
          if (textarea) {
            textarea.value = '';
          }
        });
      },
      onKeydown(event) {
        if (event.key === 'Enter') {
          if (!event.shiftKey && !event.altKey) {
            event.preventDefault();
            if (this.message.trim()) {
              console.log("onKeydown - this message: ", this.message);
              this.$emit('send-message', this.message);
              this.clearMessage();
              // Force update the v-model binding in parent
              this.$nextTick(() => {
                this.$emit('input', '');
              });
            }
          }
        } else {
          this.$emit('keydown', event);
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .chat-input-wrapper {
    position: relative;
    /* Ensure the input stays at the bottom and doesn't float */
    z-index: 10;
  }
  
  .rounded-message-input {
    border-radius: 30px !important;
    background-color: #eaeaea;
    border: 1px solid #ddd;
    position: relative;
    overflow: visible;
    margin: 10px 5px 10px 5px;
  }
  
  .dark-theme.rounded-message-input {
    background-color:#404040; 
    border-color: #666666;
  }

  .v-form {
  position: relative;
  width: 100%;
}
  
  /* Ensure the textarea background matches the container */
  :deep(.v-field) {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  
  :deep(.v-field--variant-filled) {
    background-color: transparent !important;
  }
  
  :deep(.v-field__overlay) {
    opacity: 0 !important;
    background-color: transparent !important;
  }
  
  :deep(.v-field__field) {
    background-color: transparent !important;
  }
  
  :deep(.v-field__outline) {
    opacity: 0 !important;
  }
  
  :deep(.v-textarea) {
    background-color: transparent !important;
  }
  
  :deep(.v-textarea textarea) {
    background-color: transparent !important;
    padding-right: 55px !important;
    width: calc(100% - 40px);
    padding-top: 15px;
  }
  
  .send-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute !important;
    right: 1px;
    top: 50% !important;
    transform: translateY(-50%) !important;
    z-index: 10;
  }
  
  .char-count-container {
    position: absolute;
    left: 30px;
    bottom: -20px;
    width: 100%;
    text-align: left;
  }
  
  .char-count-small {
    font-size: 10px;
    color: #666666;
  }

  .char-count-inside {
    position: absolute;
    bottom: 0px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: #666666;
    padding: 2px 8px;
    border-radius: 12px;
    z-index: 5;
    pointer-events: none; /* Makes it non-interactive */
    margin: 0;
  }
  
  /* Style the send button */
  #send-button {
    height: 36px;
    width: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e21833;
    color: white;
    border-radius: 50%;
    border: none;
    transition: all 0.2s ease;
    font-size: 16px;
  }

  #send-button svg {
  width: 19px;
  height: 19px;
  position: relative;
  top: 1px;
  right: 1px;
}
  
  #send-button:hover {
    transform: scale(1.1);
    background-color: #c01628;
    color: white;
  }
  
  #send-button:disabled {
    background-color: #cccccc;
    color: #999999;
    cursor: not-allowed;
    transform: none;
  }
  
  .transparent-textarea {
    background-color: transparent !important;
  }

  .sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
  </style>
